import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { SystemSettingsProvider } from './hooks/useSystemSettings';
import { SocketProvider } from './hooks/useSocket';
import { NotificationProvider } from './hooks/useNotifications';
import NotificationPrompt from './components/notifications/NotificationPrompt';
import NotificationsPage from './pages/NotificationsPage';
import TopbarNav from './components/layout/TopbarNav';
import MaintenanceBanner from './components/layout/MaintenanceBanner';
import ProfileCompletionBanner from './components/layout/ProfileCompletionBanner';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChatPage from './pages/ChatPage';
import MapPage from './pages/MapPage';
import Dashboard from './pages/Dashboard';
import RoutesPage from './pages/RoutesPage';
import CreateRoutePage from './pages/CreateRoutePage';
import TripsPage from './pages/TripsPage';
import CreateTripPage from './pages/CreateTripPage';
import ProfilePage from './pages/ProfilePage';
import TripRequestManagerPage from './pages/TripRequestManager';
import ApplyDriverPage from './pages/ApplyDriverPage';
import DriverApplicationStatusPage from './pages/DriverApplicationStatusPage';
import VehiclesPage from './pages/VehiclesPage';
import AdminPage from './pages/AdminPage';
import ServicesPage from './pages/ServicesPage';
import ServiceRequestPage from './pages/ServiceRequestPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ApplyServiceProviderPage from './pages/ApplyServiceProviderPage';
import BrowseServicesPage from './pages/BrowseServicesPage';
import CareersPage from './pages/CareersPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Trip from './components/trips/Trip';
import './App.css';
import ViewRoute from './pages/ViewRoute';

// Route configuration
interface RouteConfig {
  path: string;
  component: React.ComponentType;
  isPublic?: boolean;
  requiredRole?: string;
  adminOnly?: boolean;
}

const routeConfigs: RouteConfig[] = [
  // Public routes
  { path: '/', component: HomePage, isPublic: true },
  { path: '/login', component: LoginPage, isPublic: true },
  { path: '/register', component: RegisterPage, isPublic: true },
  { path: '/forgot-password', component: ForgotPasswordPage, isPublic: true },
  { path: '/reset-password', component: ResetPasswordPage, isPublic: true },
  { path: '/careers', component: CareersPage, isPublic: true },

  // Protected routes
  { path: '/routes', component: RoutesPage },
  { path: '/routes/search/:query', component: RoutesPage },
  { path: '/routes/create', component: CreateRoutePage, requiredRole: 'driver' },
  { path: '/routes/view/:id', component: ViewRoute },
  { path: '/trips', component: TripsPage },
  { path: '/trips/:routeId', component: TripsPage },
  { path: '/trips/create', component: CreateTripPage, requiredRole: 'driver' },
  { path: '/profile', component: ProfilePage },
  { path: '/notifications', component: NotificationsPage },
  { path: '/apply-driver', component: ApplyDriverPage },
  { path: '/driver-application', component: DriverApplicationStatusPage },
  { path: '/chat', component: ChatPage },
  { path: '/chat/:roomId', component: ChatPage },
  { path: '/map', component: MapPage },
  { path: '/dashboard', component: Dashboard },
  { path: '/trip/:tripId', component: Trip },
  { path: '/trip/:tripId/requests', component: TripRequestManagerPage },

  // Services routes
  { path: '/services', component: ServicesPage },
  { path: '/services/browse', component: BrowseServicesPage },
  { path: '/services/request', component: ServiceRequestPage },
  { path: '/services/:id', component: ServiceDetailPage },
  { path: '/apply-service-provider', component: ApplyServiceProviderPage },

  // Role-based routes
  { path: '/vehicles', component: VehiclesPage, requiredRole: 'driver' },

  // Admin routes
  { path: '/admin', component: AdminPage, adminOnly: true },
];

// Component to conditionally show TopbarNav
const AppLayout: React.FC = () => {
  const location = useLocation();

  // Pages where we don't want to show the topbar nav
  const hideNavRoutes = ['/login', '/register', '/forgot-password', '/reset-password'];
  const shouldShowNav = hideNavRoutes.indexOf(location.pathname) === -1;

  return (
    <div className="App">
      {shouldShowNav && <TopbarNav />}
      {shouldShowNav && <MaintenanceBanner />}
      {shouldShowNav && <ProfileCompletionBanner />}
      <Routes>
        {routeConfigs.map(({ path, component: Component, isPublic, requiredRole, adminOnly }) => (
          <Route
            key={path}
            path={path}
            element={
              isPublic ? (
                <Component />
              ) : (
                <ProtectedRoute requiredRole={requiredRole} adminOnly={adminOnly}>
                  <Component />
                </ProtectedRoute>
              )
            }
          />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SystemSettingsProvider>
        <SocketProvider>
          <NotificationProvider>
            <Router>
              <AppLayout />
              <NotificationPrompt />
            </Router>
          </NotificationProvider>
        </SocketProvider>
      </SystemSettingsProvider>
    </AuthProvider>
  );
}

export default App;

